import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, Users, Plus, Mail, Copy, Trash2, UserPlus, Crown,
  Eye, ChevronDown, ChevronUp, Clock, CheckCircle2, XCircle, Star, Monitor, KeyRound, Lock
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const roleConfig = {
  admin: { label: 'Administrador', color: 'bg-violet-500/10 text-brand-deep dark:text-brand-deep dark:text-brand-green border-violet-500/30', icon: Crown },
  manager: { label: 'Manager de Ventas', color: 'bg-brand-green/10 text-brand-deep dark:text-brand-green border-brand-green/30', icon: Star },
  viewer: { label: 'Monitor de Ventas', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', icon: Monitor },
  agent: { label: 'Agente', color: 'bg-amber-500/10 text-amber-600 border-amber-500/30', icon: Users }
};

const AdminUsers = () => {
  const { user: currentUser, isAdmin, isManager } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [tab, setTab] = useState('team');
  const [showInvite, setShowInvite] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'monitor' });
  const [lastInviteLink, setLastInviteLink] = useState('');
  const [showInviteLink, setShowInviteLink] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPasswordForUser, setNewPasswordForUser] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, invitesRes, subRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/invites`).catch(() => ({ data: [] })),
        axios.get(`${API}/subscriptions/current`).catch(() => ({ data: null }))
      ]);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
      setSubscription(subRes.data);
    } catch (err) {
      // Error handled by toast
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSendInvite = async () => {
    if (!inviteForm.email || !inviteForm.name) return;
    try {
      const res = await axios.post(`${API}/invites/send`, inviteForm);
      const link = `${window.location.origin}/login?invite=${res.data.token}`;
      setLastInviteLink(link);
      setShowInviteLink(true);
      setShowInvite(false);
      setInviteForm({ email: '', name: '', role: 'monitor' });
      toast.success(`Invitación enviada a ${inviteForm.email}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar invitación');
    }
  };

  const handleDeleteInvite = async (id) => {
    try {
      await axios.delete(`${API}/invites/${id}`);
      toast.success('Invitación revocada');
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleToggleUserActive = async (userId, active) => {
    try {
      await axios.put(`${API}/users/${userId}`, { active: !active });
      toast.success(active ? 'Usuario desactivado' : 'Usuario activado');
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleChangeRole = async (userId, newRole, userName) => {
    try {
      // Auto-update permissions for the new role
      const rolePerms = {
        admin: {
          view_dashboard: true, view_customers: true, edit_customers: true,
          view_chats: true, send_messages: true, view_campaigns: true,
          edit_campaigns: true, view_products: true, edit_products: true,
          view_analytics: true, view_financial: true, manage_users: true,
          manage_payments: true, export_data: true,
        },
        manager: {
          view_dashboard: true, view_customers: true, edit_customers: true,
          view_chats: true, send_messages: true, view_campaigns: true,
          edit_campaigns: true, view_products: true, edit_products: true,
          view_analytics: true, view_financial: true, manage_users: false,
          manage_payments: false, export_data: true,
        },
        viewer: {
          view_dashboard: true, view_customers: true, edit_customers: false,
          view_chats: true, send_messages: false, view_campaigns: true,
          edit_campaigns: false, view_products: true, edit_products: false,
          view_analytics: true, view_financial: false, manage_users: false,
          manage_payments: false, export_data: false,
        },
      };
      await axios.put(`${API}/users/${userId}`, { role: newRole, permissions: rolePerms[newRole] });
      toast.success(`${userName} ahora es ${roleConfig[newRole]?.label || newRole}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar rol');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(lastInviteLink);
    toast.success('Link copiado al portapapeles');
  };

  const handleAdminResetPassword = async () => {
    if (!resetPasswordUser || !newPasswordForUser) return;
    if (newPasswordForUser.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    try {
      await axios.post(`${API}/auth/admin-reset-password`, {
        user_id: resetPasswordUser.id,
        new_password: newPasswordForUser
      });
      toast.success(`Contraseña de ${resetPasswordUser.name} actualizada`);
      setResetPasswordUser(null);
      setNewPasswordForUser('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al resetear contraseña');
    }
  };

  const teamCounts = {
    admins: users.filter(u => u.role === 'admin').length,
    managers: users.filter(u => u.role === 'manager').length,
    monitors: users.filter(u => u.role === 'viewer').length
  };

  const plan = subscription?.plan || {};
  const canInviteManager = isAdmin;
  const canInviteMonitor = isAdmin || isManager;

  return (
    <div data-testid="admin-users-page" className="min-h-screen">
      <Header title="Equipo" subtitle="Gestión de usuarios y roles">
        {(canInviteManager || canInviteMonitor) && (
          <Button data-testid="invite-user-btn" className="gap-2 bg-primary hover:opacity-90" onClick={() => setShowInvite(true)}>
            <UserPlus className="w-4 h-4" /> Invitar Miembro
          </Button>
        )}
      </Header>

      <div className="p-6 space-y-6 max-w-5xl">
        {/* Plan & Team Counts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Crown className="w-6 h-6 mx-auto mb-2 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
              <p className="text-xs text-muted-foreground">Plan Actual</p>
              <p className="font-bold text-sm capitalize">{plan.name || 'Trial'}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Shield className="w-6 h-6 mx-auto mb-2 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
              <p className="text-xs text-muted-foreground">Administradores</p>
              <p className="font-bold">{teamCounts.admins}<span className="text-xs text-muted-foreground">/{plan.max_admins || 1}</span></p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Star className="w-6 h-6 mx-auto mb-2 text-brand-deep dark:text-brand-green" />
              <p className="text-xs text-muted-foreground">Managers</p>
              <p className="font-bold">{teamCounts.managers}<span className="text-xs text-muted-foreground">/{plan.max_managers || 1}</span></p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Monitor className="w-6 h-6 mx-auto mb-2 text-emerald-700 dark:text-emerald-400" />
              <p className="text-xs text-muted-foreground">Monitores</p>
              <p className="font-bold">{teamCounts.monitors}<span className="text-xs text-muted-foreground">/{plan.max_monitors || 4}</span></p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="team" data-testid="tab-team">Equipo ({users.length})</TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites">Invitaciones ({invites.filter(i => i.status === 'pending').length})</TabsTrigger>
          </TabsList>

          <TabsContent value="team" className="mt-4 space-y-3">
            <AnimatePresence>
              {users.map(u => {
                const cfg = roleConfig[u.role] || roleConfig.viewer;
                const RoleIcon = cfg.icon;
                const isExpanded = expandedUser === u.id;
                const isSelf = u.id === currentUser?.id;

                return (
                  <motion.div key={u.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <Card data-testid={`user-card-${u.id}`} className="glass-card overflow-hidden">
                      <CardContent className="p-0">
                        <div className="p-4 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => setExpandedUser(isExpanded ? null : u.id)}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className="bg-primary text-primary-foreground font-bold">
                                  {u.name?.charAt(0)?.toUpperCase() || '?'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-sm">{u.name}</span>
                                  {isSelf && <Badge variant="outline" className="text-[10px]">Tú</Badge>}
                                </div>
                                <p className="text-xs text-muted-foreground">{u.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className={cn("text-xs", cfg.color)}>
                                <RoleIcon className="w-3 h-3 mr-1" />{cfg.label}
                              </Badge>
                              {u.active === false && <Badge variant="destructive" className="text-xs">Inactivo</Badge>}
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </div>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-border p-4 bg-muted/10 space-y-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <p className="text-xs text-muted-foreground">Último acceso</p>
                                <p className="font-medium">{u.last_login ? new Date(u.last_login).toLocaleDateString('es') : 'Nunca'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Registrado</p>
                                <p className="font-medium">{u.created_at ? new Date(u.created_at).toLocaleDateString('es') : '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Rol</p>
                                <p className="font-medium capitalize">{cfg.label}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Estado</p>
                                <p className={cn("font-medium", u.active !== false ? "text-emerald-700 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>
                                  {u.active !== false ? 'Activo' : 'Inactivo'}
                                </p>
                              </div>
                            </div>
                            {/* Permissions */}
                            {u.permissions && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-2">Permisos</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {Object.entries(u.permissions).filter(([, v]) => v).map(([k]) => (
                                    <Badge key={k} variant="outline" className="text-[10px] bg-emerald-500/5 text-emerald-600 border-emerald-500/20">
                                      {k.replace(/_/g, ' ')}
                                    </Badge>
                                  ))}
                                  {Object.entries(u.permissions).filter(([, v]) => !v).map(([k]) => (
                                    <Badge key={k} variant="outline" className="text-[10px] bg-red-500/5 text-red-400 border-red-500/20 line-through opacity-50">
                                      {k.replace(/_/g, ' ')}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Actions */}
                            {isAdmin && !isSelf && (
                              <div className="flex flex-wrap gap-2 pt-2 items-center">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">Rol:</span>
                                  <Select value={u.role} onValueChange={(newRole) => handleChangeRole(u.id, newRole, u.name)}>
                                    <SelectTrigger className="w-[180px] h-8 text-xs" data-testid={`role-select-${u.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="admin" data-testid={`role-admin-${u.id}`}>
                                        <span className="inline-flex items-center gap-2"><Crown className="w-3 h-3" /> Administrador</span>
                                      </SelectItem>
                                      <SelectItem value="manager" data-testid={`role-manager-${u.id}`}>
                                        <span className="inline-flex items-center gap-2"><Star className="w-3 h-3" /> Manager de Ventas</span>
                                      </SelectItem>
                                      <SelectItem value="viewer" data-testid={`role-viewer-${u.id}`}>
                                        <span className="inline-flex items-center gap-2"><Monitor className="w-3 h-3" /> Monitor de Ventas</span>
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <Button size="sm" variant={u.active !== false ? "destructive" : "default"} className="text-xs"
                                  onClick={() => handleToggleUserActive(u.id, u.active !== false)}>
                                  {u.active !== false ? 'Desactivar' : 'Activar'}
                                </Button>
                                <Button
                                  data-testid={`reset-pwd-${u.id}`}
                                  size="sm"
                                  variant="outline"
                                  className="text-xs gap-1 border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
                                  onClick={() => { setResetPasswordUser(u); setNewPasswordForUser(''); }}
                                >
                                  <KeyRound className="w-3 h-3" /> Resetear Contraseña
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </TabsContent>

          <TabsContent value="invites" className="mt-4 space-y-3">
            {invites.map(inv => (
              <Card key={inv.id} data-testid={`invite-${inv.id}`} className="glass-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center",
                        inv.status === 'pending' ? "bg-amber-500/10" : "bg-emerald-500/10")}>
                        {inv.status === 'pending' ? <Clock className="w-5 h-5 text-amber-700 dark:text-amber-400" /> : <CheckCircle2 className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{inv.name}</p>
                        <p className="text-xs text-muted-foreground">{inv.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={cn("text-xs",
                        inv.role === 'manager' ? roleConfig.manager.color : roleConfig.viewer.color)}>
                        {inv.role === 'manager' ? 'Manager' : 'Monitor'}
                      </Badge>
                      <Badge variant="outline" className={cn("text-xs",
                        inv.status === 'pending' ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400')}>
                        {inv.status === 'pending' ? 'Pendiente' : 'Aceptada'}
                      </Badge>
                      {inv.status === 'pending' && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                          onClick={() => handleDeleteInvite(inv.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {invites.length === 0 && (
              <Card className="glass-card border-dashed">
                <CardContent className="py-10 text-center">
                  <Mail className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                  <p className="text-muted-foreground">No hay invitaciones</p>
                  <Button variant="outline" className="mt-3 gap-2" onClick={() => setShowInvite(true)}>
                    <UserPlus className="w-4 h-4" /> Enviar Invitación
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invitar Miembro del Equipo</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <p className="text-xs text-brand-deep dark:text-brand-deep dark:text-brand-green dark:text-brand-deep dark:text-brand-deep dark:text-brand-green">
                <Mail className="w-3 h-3 inline mr-1" />
                Se generará un link de invitación que puedes enviar por email.
                El miembro creará su contraseña al aceptar.
              </p>
            </div>
            <div>
              <Label>Nombre completo *</Label>
              <Input data-testid="invite-name" value={inviteForm.name} onChange={e => setInviteForm({...inviteForm, name: e.target.value})} placeholder="Nombre del miembro" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input data-testid="invite-email" type="email" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} placeholder="email@empresa.com" />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={inviteForm.role} onValueChange={v => setInviteForm({...inviteForm, role: v})}>
                <SelectTrigger data-testid="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {canInviteManager && <SelectItem value="manager">Manager de Ventas</SelectItem>}
                  {canInviteMonitor && <SelectItem value="monitor">Monitor de Ventas</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 text-xs space-y-1">
              <p className="font-semibold mb-1">Permisos del rol:</p>
              {inviteForm.role === 'manager' ? (
                <>
                  <p className="text-emerald-700 dark:text-emerald-400">Dashboard, Clientes (editar), Chats (enviar), Campañas (editar), Productos (editar), Analytics, Financiero, Pagos, Exportar</p>
                  <p className="text-red-400">No puede gestionar usuarios</p>
                </>
              ) : (
                <>
                  <p className="text-emerald-700 dark:text-emerald-400">Dashboard, Clientes (ver), Chats (ver), Campañas (ver), Productos (ver), Analytics</p>
                  <p className="text-red-400">No puede editar, enviar mensajes, ver financiero, gestionar pagos ni exportar</p>
                </>
              )}
            </div>
            <Button data-testid="send-invite-btn" className="w-full bg-primary"
              onClick={handleSendInvite} disabled={!inviteForm.email || !inviteForm.name}>
              <Mail className="w-4 h-4 mr-2" /> Generar Invitación
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Link Dialog */}
      <Dialog open={showInviteLink} onOpenChange={setShowInviteLink}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Link de Invitación Generado</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-sm text-emerald-600 dark:text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="w-4 h-4 inline mr-1" />
                Invitación creada. Comparte este link con el miembro del equipo.
              </p>
            </div>
            <div className="flex gap-2">
              <Input value={lastInviteLink} readOnly className="text-xs font-mono" />
              <Button variant="outline" size="icon" onClick={handleCopyLink}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              El miembro deberá abrir este link para crear su contraseña y acceder a DEAL APP con los permisos asignados.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={(open) => { if (!open) setResetPasswordUser(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-700 dark:text-amber-400" />
              Resetear Contraseña
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {resetPasswordUser && (
              <>
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-sm">
                    Vas a cambiar la contraseña de <span className="font-bold">{resetPasswordUser.name}</span>
                    <span className="text-xs text-muted-foreground block">{resetPasswordUser.email}</span>
                  </p>
                </div>
                <div>
                  <Label>Nueva Contraseña *</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="admin-reset-password-input"
                      type="password"
                      value={newPasswordForUser}
                      onChange={e => setNewPasswordForUser(e.target.value)}
                      className="pl-10"
                      placeholder="Mínimo 6 caracteres"
                    />
                  </div>
                </div>
                <Button
                  data-testid="admin-reset-password-btn"
                  className="w-full bg-brand-copper hover:opacity-90"
                  onClick={handleAdminResetPassword}
                  disabled={!newPasswordForUser || newPasswordForUser.length < 6}
                >
                  Actualizar Contraseña
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
